import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import { QRCodeCanvas } from "qrcode.react";
import { useThemeMode } from "../../ThemeContext";
import { usePermissions } from "../../contexts/PermissionContext";
import { dataAPI } from "../../helpers/api_service";
import { APP_URLS, getCompanyLogoPathFromSession } from "../../helpers/url_helper";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.complimate";
const APP_STORE_URL = "https://apps.apple.com/us/app/complimate-app/id6756040647";
const PDF_HEADER_LINE_COLOR = [0, 147, 145];
const PDF_FIELD_LINE_COLOR = [226, 232, 240];

const drawCompanyFormPdfHeader = (
  doc,
  { pageWidth, margin, contentWidth, logoImage, title, logoMaxHeight = 44, logoMaxWidth = 110 },
) => {
  const headerTop = margin;
  let logoBlockHeight = 0;

  if (logoImage?.dataUrl) {
    try {
      const aspect = logoImage.width / logoImage.height;
      let logoH = logoMaxHeight;
      let logoW = logoH * aspect;
      if (logoW > logoMaxWidth) {
        logoW = logoMaxWidth;
        logoH = logoW / aspect;
      }
      const logoX = pageWidth - margin - logoW;
      doc.addImage(
        logoImage.dataUrl,
        logoImage.format,
        logoX,
        headerTop,
        logoW,
        logoH,
      );
      logoBlockHeight = logoH;
    } catch {
      logoBlockHeight = 0;
    }
  }

  const formTitle = title || "Company Form";
  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(15, 23, 42);
  const titleLines = doc.splitTextToSize(formTitle, contentWidth);
  const titleLineHeight = 22;
  const titleBlockHeight = titleLines.length * titleLineHeight;
  const blockHeight = Math.max(logoBlockHeight, titleBlockHeight);
  const titleStartY =
    headerTop + (blockHeight - titleBlockHeight) / 2 + titleLineHeight - 6;

  titleLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, titleStartY + index * titleLineHeight, {
      align: "center",
    });
  });

  const headerBottom = headerTop + blockHeight + 14;
  doc.setDrawColor(...PDF_HEADER_LINE_COLOR);
  doc.setLineWidth(1.2);
  doc.line(margin, headerBottom, pageWidth - margin, headerBottom);
  doc.setLineWidth(1);

  return headerBottom + 18;
};

function CompanyFormList() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const { hasReadPermission } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const [qrModalForm, setQrModalForm] = useState(null);
  const qrCanvasRef = useRef(null);
  const playStoreQrRef = useRef(null);
  const appStoreQrRef = useRef(null);
  const cachedLogoPdfRef = useRef(null);
  const showFormRecordsColumn = hasReadPermission("companyforms");

  useEffect(() => {
    let mounted = true;

    const loadTemplates = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await dataAPI.getDigitalFormClientForms();
        if (!mounted) return;
        setTemplates(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(error?.message || "Failed to load company forms.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, []);

  const emptyText = useMemo(() => {
    if (errorMessage) return errorMessage;
    return "No company forms available.";
  }, [errorMessage]);

  const activeTemplates = templates.filter((item) => item?.is_active !== false);
  const filteredTemplates = activeTemplates.filter((item) =>
    String(item?.title || "")
      .toLowerCase()
      .includes(searchTitle.trim().toLowerCase()),
  );

  const safeFileName = (value) =>
    String(value || "company-form")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "company-form";

  const getQrCodeValue = (template) => {
    const qrCodeId = String(template?.qr_code_id || "").trim();
    if (!qrCodeId) return "";
    return `${window.location.origin}${APP_URLS.COMPANY_FORM_FILL}?qr_code_id=${encodeURIComponent(qrCodeId)}`;
  };

  const openQrModal = (template) => {
    if (!String(template?.qr_code_id || "").trim()) return;
    setQrModalForm(template);
  };

  const closeQrModal = () => {
    setQrModalForm(null);
  };

  const buildLogoDataUrl = (base64Value, filePath, apiData = {}) => {
    const raw = String(base64Value || "").trim();
    if (!raw) return "";

    if (raw.startsWith("data:image")) {
      return raw;
    }

    const path = filePath || apiData?.file_path || "";
    const contentType = String(apiData?.content_type || apiData?.mime_type || "").toLowerCase();
    const isPng =
      contentType.includes("png") || /\.png$/i.test(path);
    const mime = isPng ? "image/png" : "image/jpeg";
    return `data:${mime};base64,${raw}`;
  };

  const dataUrlToPdfLogoData = async (dataUrl) => {
    if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
      return null;
    }

    const format = dataUrl.includes("image/png") ? "PNG" : "JPEG";

    const dimensions = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () =>
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      img.onerror = () => resolve({ width: 120, height: 60 });
      img.src = dataUrl;
    });

    return { dataUrl, format, ...dimensions };
  };

  const loadCompanyLogoForPdf = async () => {
    const filePath = getCompanyLogoPathFromSession();
    if (!filePath) return null;

    try {
      const response = await dataAPI.getMediaPresignedUrl(filePath, {
        showLoading: false,
      });
      const apiData = response?.data;
      const inlineBase64 =
        apiData?.base64 ||
        apiData?.file_base64 ||
        apiData?.image_base64 ||
        apiData?.content_base64;

      if (!inlineBase64) return null;

      const dataUrl = buildLogoDataUrl(inlineBase64, filePath, apiData);
      return dataUrlToPdfLogoData(dataUrl);
    } catch {
      return null;
    }
  };

  const resolveLogoForPdf = async () => {
    if (cachedLogoPdfRef.current?.dataUrl) {
      return cachedLogoPdfRef.current;
    }

    const loaded = await loadCompanyLogoForPdf();
    if (loaded?.dataUrl) {
      cachedLogoPdfRef.current = loaded;
    }
    return loaded;
  };

  const downloadQrCode = async () => {
    const doc = await buildQrPrintPdf();
    if (!doc) return;
    doc.save(`${safeFileName(qrModalForm?.title)}-qr-code.pdf`);
  };

  const buildQrPrintPdf = async () => {
    const formCanvas = qrCanvasRef.current;
    const playStoreCanvas = playStoreQrRef.current;
    const appStoreCanvas = appStoreQrRef.current;
    if (!formCanvas) return null;

    const logoImage = await resolveLogoForPdf();

    const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "portrait" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    const footerHeight = 130;
    const footerTop = pageHeight - margin - footerHeight;

    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, pageWidth, pageHeight, "F");

    const description = String(qrModalForm?.description || "").trim();

    let y = drawCompanyFormPdfHeader(doc, {
      pageWidth,
      margin,
      contentWidth,
      logoImage,
      title: qrModalForm?.title || "Company Form",
    });

    if (description) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      doc.setTextColor(100, 116, 139);
      const descLines = doc.splitTextToSize(description, contentWidth);
      descLines.forEach((line) => {
        doc.text(line, pageWidth / 2, y, { align: "center" });
        y += 16;
      });
      y += 12;
    }

    const smallQrSize = 64;
    const storeGap = 90;
    const storeLabelGap = 18;
    const availableQrHeight = footerTop - y - 20;
    const mainQrSize = Math.min(260, contentWidth - 48, availableQrHeight);
    const mainQrX = (pageWidth - mainQrSize) / 2;
    const mainQrY = y + Math.max(0, (availableQrHeight - mainQrSize) / 2);

    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(
      mainQrX - 14,
      mainQrY - 14,
      mainQrSize + 28,
      mainQrSize + 28,
      10,
      10,
      "FD",
    );
    doc.addImage(
      formCanvas.toDataURL("image/png"),
      "PNG",
      mainQrX,
      mainQrY,
      mainQrSize,
      mainQrSize,
    );

    doc.setDrawColor(226, 232, 240);
    doc.line(margin, footerTop, pageWidth - margin, footerTop);

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(15, 23, 42);
    doc.text("Download the Complimate App", pageWidth / 2, footerTop + 20, { align: "center" });

    const storesTotalWidth = smallQrSize * 2 + storeGap;
    const storesStartX = (pageWidth - storesTotalWidth) / 2;
    const storeQrY = footerTop + 36;

    const drawStoreQr = (canvas, x, label) => {
      if (!canvas) return;
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(226, 232, 240);
      doc.roundedRect(x - 6, storeQrY - 6, smallQrSize + 12, smallQrSize + 12, 6, 6, "FD");
      doc.addImage(
        canvas.toDataURL("image/png"),
        "PNG",
        x,
        storeQrY,
        smallQrSize,
        smallQrSize,
      );
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(51, 65, 85);
      doc.text(label, x + smallQrSize / 2, storeQrY + smallQrSize + storeLabelGap, { align: "center" });
    };

    drawStoreQr(playStoreCanvas, storesStartX, "Google Play");
    drawStoreQr(appStoreCanvas, storesStartX + smallQrSize + storeGap, "App Store");

    return doc;
  };

  const printQrSheet = async () => {
    const doc = await buildQrPrintPdf();
    if (!doc) return;

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const printWindow = window.open(pdfUrl, "_blank");

    if (printWindow) {
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      doc.save(`${safeFileName(qrModalForm?.title)}-qr-print.pdf`);
    }

    window.setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
  };

  useEffect(() => {
    let cancelled = false;
    resolveLogoForPdf().then((logoData) => {
      if (!cancelled && logoData?.dataUrl) {
        cachedLogoPdfRef.current = logoData;
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getTemplateSections = (template) => {
    const source = Array.isArray(template?.schema_json) ? template.schema_json : [];
    if (!source.length) return [];
    const isSectionSchema = Array.isArray(source[0]?.fields);
    if (isSectionSchema) {
      return source.map((section, sectionIdx) => ({
        id: section?.id || `section-${sectionIdx + 1}`,
        title: section?.title || `Section ${sectionIdx + 1}`,
        description: section?.description || "",
        fields: Array.isArray(section?.fields) ? section.fields : [],
      }));
    }
    return [
      {
        id: "section-1",
        title: "Section 1",
        description: "",
        fields: source,
      },
    ];
  };

  const downloadTemplatePdf = async (template) => {
    const logoImage = await resolveLogoForPdf();
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const ensurePageSpace = (required = 24) => {
      if (y + required > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addWrappedText = (text, fontSize = 11, gap = 8) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(String(text || ""), contentWidth);
      const lineHeight = fontSize + 4;
      ensurePageSpace(lines.length * lineHeight + gap);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + gap;
    };

    const drawFieldLine = (lineY, fromX = margin, toX = pageWidth - margin) => {
      ensurePageSpace(20);
      doc.setDrawColor(...PDF_FIELD_LINE_COLOR);
      doc.setLineWidth(1);
      doc.line(fromX, lineY, toX, lineY);
    };

    y = drawCompanyFormPdfHeader(doc, {
      pageWidth,
      margin,
      contentWidth,
      logoImage,
      title: template?.title || "Company Form",
    });

    doc.setFont("helvetica", "normal");
    doc.setTextColor(51, 65, 85);
    addWrappedText(template?.description || "No description", 11, 20);

    const sections = getTemplateSections(template);
    let questionNumber = 1;

    sections.forEach((section, sectionIndex) => {
      doc.setFont("helvetica", "bold");
      addWrappedText(section?.title || `Section ${sectionIndex + 1}`, 14, 4);
      doc.setFont("helvetica", "normal");
      if (section?.description) {
        addWrappedText(section.description, 10, 8);
      }

      (section?.fields || []).forEach((field) => {
        const fieldLabel = field?.label || "Untitled Field";
        const fieldType = String(field?.type || "text").toLowerCase();

        doc.setFont("helvetica", "bold");
        addWrappedText(`${questionNumber}. ${fieldLabel}`, 12, 8);
        doc.setFont("helvetica", "normal");

        if (
          fieldType === "radio" ||
          fieldType === "checkbox" ||
          fieldType === "dropdown" ||
          fieldType === "multi_select"
        ) {
          const options = Array.isArray(field?.options) ? field.options : [];
          const hasDataSource = String(field?.dataSource || "").trim().length > 0;
          if (hasDataSource) {
            addWrappedText(
              `(Options loaded from data source: ${field.dataSource})`,
              10,
              6,
            );
          } else {
            const printableOptions = options.length ? options : ["Option 1", "Option 2"];
            printableOptions.forEach((option) => {
              addWrappedText(`[   ] ${option}`, 10, 4);
            });
          }
          y += 14;
          questionNumber += 1;
          return;
        }

        if (fieldType === "attachment") {
          addWrappedText("Attachment upload field", 10, 6);
          drawFieldLine(y + 14);
          y += 28;
          questionNumber += 1;
          return;
        }

        if (fieldType === "textarea") {
          ensurePageSpace(110);
          drawFieldLine(y + 14);
          drawFieldLine(y + 36);
          drawFieldLine(y + 58);
          drawFieldLine(y + 80);
          y += 92;
          questionNumber += 1;
          return;
        }

        ensurePageSpace(44);
        drawFieldLine(y + 14);
        y += 38;
        questionNumber += 1;
      });

      y += 6;
    });

    doc.save(`${safeFileName(template?.title)}.pdf`);
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={containerStyle(mode)}>
        <div style={{ marginBottom: 14 }}>
          <input
            type="text"
            value={searchTitle}
            onChange={(event) => setSearchTitle(event.target.value)}
            placeholder="Search by form title..."
            style={searchInputStyle(mode)}
          />
        </div>

        {!loading && !filteredTemplates.length ? (
          <div style={emptyCardStyle(mode)}>{emptyText}</div>
        ) : (
          <div style={tableWrapperStyle(mode)}>
            <table style={tableStyle()}>
              <colgroup>
                <col style={{ width: 180 }} />
                <col />
                <col style={{ width: 72 }} />
                <col style={{ width: 230 }} />
                <col style={{ width: 128 }} />
                {showFormRecordsColumn && <col style={{ width: 128 }} />}
              </colgroup>
              <thead>
                <tr>
                  <th style={headCellStyle(mode)}>Title</th>
                  <th style={headCellStyle(mode)}>Description</th>
                  <th style={{ ...headCellStyle(mode), ...compactColumnCellStyle() }}>Fields</th>
                  <th style={{ ...headCellStyle(mode), ...compactColumnCellStyle() }}>Action</th>
                  <th style={{ ...headCellStyle(mode), ...compactColumnCellStyle(), textAlign: "center" }}>
                    QR Code
                  </th>
                  {showFormRecordsColumn && (
                    <th style={{ ...headCellStyle(mode), ...compactColumnCellStyle(), textAlign: "center" }}>
                      Form records
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id} style={rowStyle(mode)}>
                    <td style={bodyCellStyle(mode)}>
                      {template?.title || "Untitled Form"}
                    </td>
                    <td style={descriptionCellStyle(mode)}>
                      <div
                        style={descriptionClampStyle()}
                        title={template?.description || "No description"}
                      >
                        {template?.description || "No description"}
                      </div>
                    </td>
                    <td style={{ ...bodyCellStyle(mode), ...compactColumnCellStyle() }}>
                      {getTemplateSections(template).reduce(
                        (total, section) =>
                          total + (Array.isArray(section?.fields) ? section.fields.length : 0),
                        0,
                      )}
                    </td>
                    <td style={{ ...bodyCellStyle(mode), ...compactColumnCellStyle() }}>
                      <div style={actionWrapStyle()}>
                        <button
                          type="button"
                          style={openBtnStyle()}
                          onClick={() =>
                            navigate(APP_URLS.COMPANY_FORM_FILL, {
                              state: { template },
                            })
                          }
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          style={downloadBtnStyle()}
                          onClick={() => downloadTemplatePdf(template)}
                        >
                          Download PDF
                        </button>
                      </div>
                    </td>
                    <td style={{ ...bodyCellStyle(mode), ...compactColumnCellStyle(), textAlign: "center" }}>
                      <button
                        type="button"
                        style={qrBtnStyle(Boolean(template?.qr_code_id))}
                        disabled={!String(template?.qr_code_id || "").trim()}
                        onClick={() => openQrModal(template)}
                      >
                        Generate QR
                      </button>
                    </td>
                    {showFormRecordsColumn && (
                      <td style={{ ...bodyCellStyle(mode), ...compactColumnCellStyle(), textAlign: "center" }}>
                        <button
                          type="button"
                          style={recordsBtnStyle()}
                          onClick={() =>
                            navigate(APP_URLS.COMPANY_FORM_RECORDS, {
                              state: { form: template },
                            })
                          }
                        >
                          View Records
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {qrModalForm && (
        <div
          style={qrModalOverlayStyle()}
          onClick={closeQrModal}
          role="dialog"
          aria-modal="true"
          aria-labelledby="qr-modal-title"
        >
          <div
            style={qrModalCardStyle(mode)}
            onClick={(event) => event.stopPropagation()}
          >
            <div style={qrModalHeaderStyle(mode)}>
              <div style={qrModalHeaderTextStyle()}>
                <QrCode2Icon sx={{ fontSize: 22, color: "#009391" }} />
                <h2 id="qr-modal-title" style={qrModalTitleStyle(mode)}>
                  Form QR Code
                </h2>
              </div>
              <button
                type="button"
                onClick={closeQrModal}
                style={qrModalCloseBtnStyle(mode)}
                aria-label="Close"
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </button>
            </div>

            <div style={qrModalBodyStyle()}>
              <p style={qrModalFormTitleStyle(mode)}>
                {qrModalForm?.title || "Untitled Form"}
              </p>
              {qrModalForm?.description ? (
                <p style={qrModalFormDescStyle(mode)}>{qrModalForm.description}</p>
              ) : null}

              <div style={qrCodeFrameStyle(mode)}>
                <QRCodeCanvas
                  ref={qrCanvasRef}
                  value={getQrCodeValue(qrModalForm)}
                  size={220}
                  level="M"
                  includeMargin
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                />
              </div>

              <div style={qrAppStoresSectionStyle(mode)}>
                <p style={qrAppStoresTitleStyle(mode)}>Download the Complimate App</p>
                <div style={qrAppStoresRowStyle()}>
                  <div style={qrAppStoreItemStyle()}>
                    <div style={qrAppStoreQrFrameStyle(mode)}>
                      <QRCodeCanvas
                        ref={playStoreQrRef}
                        value={PLAY_STORE_URL}
                        size={72}
                        level="M"
                        includeMargin
                        bgColor="#ffffff"
                        fgColor="#0f172a"
                      />
                    </div>
                    <span style={qrAppStoreLabelStyle(mode)}>Google Play</span>
                  </div>
                  <div style={qrAppStoreItemStyle()}>
                    <div style={qrAppStoreQrFrameStyle(mode)}>
                      <QRCodeCanvas
                        ref={appStoreQrRef}
                        value={APP_STORE_URL}
                        size={72}
                        level="M"
                        includeMargin
                        bgColor="#ffffff"
                        fgColor="#0f172a"
                      />
                    </div>
                    <span style={qrAppStoreLabelStyle(mode)}>App Store</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={qrModalFooterStyle(mode)}>
              <button type="button" style={qrModalCancelBtnStyle(mode)} onClick={closeQrModal}>
                Close
              </button>
              <button type="button" style={qrModalPrintBtnStyle()} onClick={printQrSheet}>
                <PrintIcon sx={{ fontSize: 18 }} />
                Print
              </button>
              <button type="button" style={qrModalDownloadBtnStyle()} onClick={downloadQrCode}>
                <DownloadIcon sx={{ fontSize: 18 }} />
                Download QR
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

const containerStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  width: "100%",
});

const searchInputStyle = (mode) => ({
  width: "100%",
  maxWidth: 360,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const emptyCardStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const tableWrapperStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  borderRadius: 10,
  overflowX: "auto",
  width: "100%",
});

const tableStyle = () => ({
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
});

const compactColumnCellStyle = () => ({
  width: "1%",
  whiteSpace: "nowrap",
});

const headCellStyle = (mode) => ({
  textAlign: "left",
  padding: "11px 12px",
  borderBottom: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  background: mode === "dark" ? "#171717" : "#f8fafc",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
  fontWeight: 600,
});

const rowStyle = (mode) => ({
  borderBottom: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
});

const bodyCellStyle = (mode) => ({
  padding: "11px 12px",
  color: mode === "dark" ? "#d0d5dd" : "#0f172a",
  fontSize: 13,
});

const descriptionCellStyle = (mode) => ({
  ...bodyCellStyle(mode),
  whiteSpace: "normal",
  wordBreak: "break-word",
  verticalAlign: "top",
});

const descriptionClampStyle = () => ({
  display: "-webkit-box",
  WebkitLineClamp: 3,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: 1.45,
  maxHeight: "4.35em",
});

const openBtnStyle = () => ({
  border: "none",
  background: "#009391",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const downloadBtnStyle = () => ({
  border: "none",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const recordsBtnStyle = () => ({
  border: "none",
  background: "#0ea5a4",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const actionWrapStyle = () => ({
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
});

const qrBtnStyle = (hasQrCode) => ({
  border: "none",
  background: hasQrCode ? "#009391" : "#94a3b8",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: hasQrCode ? "pointer" : "not-allowed",
  opacity: hasQrCode ? 1 : 0.7,
});

const qrModalOverlayStyle = () => ({
  position: "fixed",
  inset: 0,
  zIndex: 10000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(15, 23, 42, 0.55)",
  padding: 24,
  overflowY: "auto",
});

const qrModalCardStyle = (mode) => ({
  background: mode === "dark" ? "#1a1a1a" : "#ffffff",
  borderRadius: 14,
  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.22)",
  maxWidth: 480,
  width: "100%",
  maxHeight: "calc(100vh - 48px)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  margin: "auto",
});

const qrModalHeaderStyle = (mode) => ({
  padding: "18px 20px",
  borderBottom: `1px solid ${mode === "dark" ? "#333" : "#e2e8f0"}`,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexShrink: 0,
});

const qrModalHeaderTextStyle = () => ({
  display: "flex",
  alignItems: "center",
  gap: 8,
});

const qrModalTitleStyle = (mode) => ({
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  color: mode === "dark" ? "#fff" : "#0f172a",
});

const qrModalCloseBtnStyle = (mode) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  padding: 4,
  color: mode === "dark" ? "#94a3b8" : "#64748b",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const qrModalBodyStyle = () => ({
  padding: "22px 20px 8px",
  textAlign: "center",
  flex: 1,
  minHeight: 0,
  overflowY: "auto",
  WebkitOverflowScrolling: "touch",
});

const qrModalFormTitleStyle = (mode) => ({
  margin: "0 0 6px",
  fontSize: 17,
  fontWeight: 700,
  color: mode === "dark" ? "#f8fafc" : "#0f172a",
});

const qrModalFormDescStyle = (mode) => ({
  margin: "0 0 16px",
  fontSize: 13,
  color: mode === "dark" ? "#94a3b8" : "#64748b",
  lineHeight: 1.45,
});

const qrCodeFrameStyle = (mode) => ({
  display: "inline-flex",
  padding: 14,
  borderRadius: 12,
  background: "#fff",
  border: `1px solid ${mode === "dark" ? "#334155" : "#e2e8f0"}`,
  marginBottom: 14,
});

const qrModalFooterStyle = (mode) => ({
  padding: "16px 20px 20px",
  borderTop: `1px solid ${mode === "dark" ? "#333" : "#e2e8f0"}`,
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 10,
  flexShrink: 0,
  background: mode === "dark" ? "#1a1a1a" : "#ffffff",
});

const qrModalCancelBtnStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  background: "transparent",
  color: mode === "dark" ? "#e2e8f0" : "#334155",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  cursor: "pointer",
});

const qrModalDownloadBtnStyle = () => ({
  border: "none",
  background: "#009391",
  color: "#fff",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const qrModalPrintBtnStyle = () => ({
  border: "none",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 8,
  fontSize: 13,
  fontWeight: 600,
  padding: "8px 14px",
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
});

const qrAppStoresSectionStyle = (mode) => ({
  marginTop: 20,
  paddingTop: 18,
  borderTop: `1px solid ${mode === "dark" ? "#334155" : "#e2e8f0"}`,
});

const qrAppStoresTitleStyle = (mode) => ({
  margin: "0 0 14px",
  fontSize: 13,
  fontWeight: 600,
  color: mode === "dark" ? "#e2e8f0" : "#334155",
});

const qrAppStoresRowStyle = () => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "flex-start",
  gap: 32,
});

const qrAppStoreItemStyle = () => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 8,
});

const qrAppStoreQrFrameStyle = (mode) => ({
  display: "inline-flex",
  padding: 8,
  borderRadius: 10,
  background: "#fff",
  border: `1px solid ${mode === "dark" ? "#334155" : "#e2e8f0"}`,
});

const qrAppStoreLabelStyle = (mode) => ({
  fontSize: 12,
  fontWeight: 600,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
});

export default CompanyFormList;
